import React, { useEffect, useState } from 'react';
import {
  getNodes, registerNode, updateNode, deleteNode,
  getNodeCameras, getNodeAlerts, getNodeGroups, getNodeModelAssignments,
} from '../services/api.service';
import NodeSummaryRow, { type NodeDetail } from '../components/dashboard/NodeSummaryRow';
import { useAuth } from '../contexts/AuthContext';

/** Value of a fulfilled settled result, or undefined if it rejected. */
const settled = (r: PromiseSettledResult<any>) => (r.status === 'fulfilled' ? r.value : undefined);

/**
 * Normalise the node-proxy's response shapes to an array. Different endpoints
 * return a bare array, `{ data: [...] }`, or `{ data: [...], pagination }`.
 */
function toArray(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

interface NodeInfo {
  nodeId: string;
  name: string;
  status: 'online' | 'offline';
  tunnelActive: boolean;
  lastSeen: number;
  metrics: any;
  version: string;
  capabilities: string[];
  activeSessions: number;
}

export default function Dashboard() {
  const [nodes, setNodes] = useState<NodeInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerForm, setRegisterForm] = useState({ nodeId: '', name: '', authToken: '' });
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');

  // Edit state
  const [editNode, setEditNode] = useState<NodeInfo | null>(null);
  const [editForm, setEditForm] = useState({ name: '', authToken: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<NodeInfo | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Per-node detail powering each node row's mini cards. One map (rather than one
  // state per data type) keeps a node's card filling in atomically and keeps the
  // fan-out to a single batched effect.
  const [nodeDetails, setNodeDetails] = useState<Record<string, NodeDetail>>({});

  // Node selection + navigation now live in NodeSummaryRow: each mini card must
  // select its own node before routing, so the page no longer needs either.
  const { hasPermission } = useAuth();

  const fetchNodes = async () => {
    try {
      const data = await getNodes();
      setNodes(data.nodes || []);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to fetch sites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNodes();
    // NOTE: NodeContext already polls getNodes() every 10s globally.
    // We only fetch once on mount here; rely on NodeContext for live updates
    // if Dashboard consumes useNode(). Keep a slower 30s refresh as fallback
    // for the local `nodes` state used by camera fetching below.
    const interval = setInterval(() => {
      if (!document.hidden) fetchNodes();
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  /**
   * One batched fan-out for every online node's card.
   *
   * Each of the four reads is a separate tunnel round-trip, so nodes are fetched
   * BATCH_SIZE at a time rather than all at once. Within a node the four run
   * concurrently under `allSettled`: one denied or timed-out read (a permission
   * the user lacks, a cold tunnel) leaves that one mini card empty instead of
   * blanking the whole row.
   */
  useEffect(() => {
    const onlineNodes = nodes.filter((n) => n.status === 'online');
    if (onlineNodes.length === 0) return;

    const canAlerts = hasPermission('alerts.view');
    const canGroups = hasPermission('groups.view');
    const canModels = hasPermission('ai-models.view');

    let cancelled = false;

    // Show skeletons only for nodes we have nothing for yet, so a refresh doesn't
    // flash the whole list back to loading.
    setNodeDetails((prev) => {
      const next = { ...prev };
      for (const n of onlineNodes) {
        if (!next[n.nodeId]) next[n.nodeId] = { cameras: [], groups: [], assignments: {}, alerts: [], loading: true };
      }
      return next;
    });

    (async () => {
      const results: Record<string, NodeDetail> = {};
      const BATCH_SIZE = 5;

      for (let i = 0; i < onlineNodes.length; i += BATCH_SIZE) {
        if (cancelled) return;
        const batch = onlineNodes.slice(i, i + BATCH_SIZE);
        await Promise.allSettled(
          batch.map(async (node) => {
            const id = node.nodeId;
            const [cams, groups, assignments, alerts] = await Promise.allSettled([
              getNodeCameras(id),
              canGroups ? getNodeGroups(id) : Promise.resolve([]),
              canModels ? getNodeModelAssignments(id) : Promise.resolve({}),
              canAlerts ? getNodeAlerts(id, { limit: 5, sort: 'desc' }) : Promise.resolve({ data: [] }),
            ]);

            results[id] = {
              cameras: toArray(settled(cams)),
              groups: toArray(settled(groups)),
              assignments: settled(assignments) || {},
              alerts: toArray(settled(alerts)),
              loading: false,
            };
          })
        );
        // Commit each batch as it lands so early rows render while later ones fetch.
        if (!cancelled) setNodeDetails((prev) => ({ ...prev, ...results }));
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes.map((n) => n.nodeId + n.status).join(',')]);

  const handleRegisterNode = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterLoading(true);
    setRegisterError('');
    setRegisterSuccess('');
    try {
      await registerNode(registerForm.nodeId, registerForm.name, registerForm.authToken);
      // Wait for the node list to refresh so the whole registration flow is done
      // before we surface success — no flash of the editable form in between.
      await fetchNodes();
      setRegisterForm({ nodeId: '', name: '', authToken: '' });
      setRegisterSuccess(`Site "${registerForm.name}" registered successfully!`);
      // Keep the loader/disabled state right up until the modal closes.
      setTimeout(() => {
        setShowRegisterModal(false);
        setRegisterSuccess('');
        setRegisterLoading(false);
      }, 1500);
    } catch (err: any) {
      setRegisterError(err.message || 'Failed to register site');
      setRegisterLoading(false);
    }
  };

  const generateId = () => {
    const id = crypto.randomUUID?.() || Math.random().toString(36).substring(2, 15);
    setRegisterForm(prev => ({ ...prev, nodeId: id }));
  };

  const generateToken = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let token = '';
    for (let i = 0; i < 48; i++) token += chars.charAt(Math.floor(Math.random() * chars.length));
    setRegisterForm(prev => ({ ...prev, authToken: token }));
  };

  const handleEditNode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editNode) return;
    setEditLoading(true);
    setEditError('');
    try {
      await updateNode(editNode.nodeId, {
        name: editForm.name,
        authToken: editForm.authToken || undefined,
      });
      setEditNode(null);
      fetchNodes();
    } catch (err: any) {
      setEditError(err.message || 'Failed to update site');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteNode = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await deleteNode(deleteTarget.nodeId);
      setDeleteTarget(null);
      fetchNodes();
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete site');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEditModal = (node: NodeInfo) => {
    setEditForm({ name: node.name, authToken: '' });
    setEditError('');
    setEditNode(node);
  };

  const openDeleteModal = (node: NodeInfo) => {
    setDeleteError('');
    setDeleteTarget(node);
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-foreground">Dashboard</h1>
          {hasPermission('nodes.register') && (
            <button
              onClick={() => { setShowRegisterModal(true); setRegisterError(''); setRegisterSuccess(''); }}
              className="bg-brand hover:bg-brand-dark text-white text-[13px] font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Register Site
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 bg-[var(--destructive)]/10 border border-[var(--destructive)]/30 text-[var(--destructive)] px-4 py-2 rounded-lg text-[13px]">
          {error}
        </div>
      )}

      {/* Site Grid */}
      <div>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-brand"></div>
            <span className="ml-3 text-muted-foreground text-[13px]">Loading sites…</span>
          </div>
        ) : nodes.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
            </div>
            <p className="text-lg mb-2 text-foreground">No sites registered</p>
            <p className="text-[13px] mb-6">Register a TRINETRA site to get started</p>
            {hasPermission('nodes.register') && (
              <button
                onClick={() => { setShowRegisterModal(true); setRegisterError(''); setRegisterSuccess(''); }}
                className="bg-brand hover:bg-brand-dark text-white font-medium px-6 py-2.5 rounded-lg transition-colors inline-flex items-center gap-2 text-[13px]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Register Your First Site
              </button>
            )}
          </div>
        ) : (
          // One full-width row per node — online first, so a long offline tail
          // doesn't push the live nodes below the fold.
          <div className="space-y-4">
            {[...nodes]
              .sort((a, b) => Number(b.status === 'online') - Number(a.status === 'online'))
              .map((node) => (
                <NodeSummaryRow
                  key={node.nodeId}
                  node={node}
                  detail={nodeDetails[node.nodeId]}
                  hasPermission={hasPermission}
                  canEdit={hasPermission('nodes.edit')}
                  canDelete={hasPermission('nodes.delete')}
                  onEdit={() => openEditModal(node)}
                  onDelete={() => openDeleteModal(node)}
                />
              ))}
          </div>
        )}
      </div>

      {/* Register Site Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl w-full max-w-md border border-border shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">Register Site</h2>
              <button
                onClick={() => setShowRegisterModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleRegisterNode} className="px-6 py-5 space-y-4">
              {registerError && (
                <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm px-3 py-2 rounded-lg">
                  {registerError}
                </div>
              )}
              {registerSuccess && (
                <div className="bg-[var(--success)]/10 border border-[var(--success)]/30 text-[var(--success)] text-sm px-3 py-2 rounded-lg">
                  {registerSuccess}
                </div>
              )}

              {/* Site ID */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Site ID</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={registerForm.nodeId}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, nodeId: e.target.value }))}
                    placeholder="e.g., site-office-01"
                    required
                    className="flex-1 bg-input border border-border text-foreground text-sm rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand focus:border-transparent outline-none placeholder-muted-foreground"
                  />
                  <button
                    type="button"
                    onClick={generateId}
                    className="bg-muted hover:bg-accent text-foreground text-xs px-3 py-2.5 rounded-lg transition-colors whitespace-nowrap"
                  >
                    Generate
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Unique identifier for this site. Must match the Site ID set on the machine itself.</p>
              </div>

              {/* Site Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Site Name</label>
                <input
                  type="text"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Main Office NVR"
                  required
                  className="w-full bg-input border border-border text-foreground text-sm rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand focus:border-transparent outline-none placeholder-muted-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">A friendly display name for the site.</p>
              </div>

              {/* Auth Token */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Auth Token</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={registerForm.authToken}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, authToken: e.target.value }))}
                    placeholder="Shared secret for site authentication"
                    required
                    className="flex-1 bg-input border border-border text-foreground text-sm rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand focus:border-transparent outline-none placeholder-muted-foreground font-mono"
                  />
                  <button
                    type="button"
                    onClick={generateToken}
                    className="bg-muted hover:bg-accent text-foreground text-xs px-3 py-2.5 rounded-lg transition-colors whitespace-nowrap"
                  >
                    Generate
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">This token must also be entered on the machine, under Settings → Cloud Connectivity.</p>
              </div>

              {/* Info box */}
              <div className="bg-[var(--info)]/10 border border-[var(--info)]/30 rounded-lg p-3">
                <p className="text-xs text-[var(--info)] leading-relaxed">
                  <strong>Next step:</strong> On the machine itself, open <code className="bg-[var(--info)]/20 px-1 rounded">Settings → Cloud Connectivity</code> and enter this Site ID and Auth Token to connect it to the cloud.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowRegisterModal(false)}
                  className="flex-1 bg-muted hover:bg-accent text-foreground font-medium py-2.5 rounded-lg transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={registerLoading}
                  className="flex-1 bg-brand hover:bg-brand-dark disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                >
                  {registerSuccess ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      Registered
                    </>
                  ) : registerLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white" />
                      Registering...
                    </>
                  ) : (
                    'Register Site'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Site Modal */}
      {editNode && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl w-full max-w-md border border-border shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">Edit Site</h2>
              <button onClick={() => setEditNode(null)} className="text-muted-foreground hover:text-foreground transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form onSubmit={handleEditNode} className="px-6 py-5 space-y-4">
              {editError && (
                <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm px-3 py-2 rounded-lg">{editError}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Site ID</label>
                <input
                  type="text"
                  value={editNode.nodeId}
                  disabled
                  className="w-full bg-muted border border-border text-muted-foreground text-sm rounded-lg px-3 py-2.5 cursor-not-allowed font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">Site ID cannot be changed.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Site Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full bg-input border border-border text-foreground text-sm rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand focus:border-transparent outline-none placeholder-muted-foreground"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Auth Token <span className="text-muted-foreground font-normal">(leave blank to keep current)</span></label>
                <input
                  type="text"
                  value={editForm.authToken}
                  onChange={(e) => setEditForm(prev => ({ ...prev, authToken: e.target.value }))}
                  placeholder="Enter new token to change"
                  className="w-full bg-input border border-border text-foreground text-sm rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand focus:border-transparent outline-none placeholder-muted-foreground font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">If changed, update the machine's Cloud Connectivity settings to match.</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditNode(null)} className="flex-1 bg-muted hover:bg-accent text-foreground font-medium py-2.5 rounded-lg transition-colors text-sm">Cancel</button>
                <button type="submit" disabled={editLoading} className="flex-1 bg-brand hover:bg-brand-dark disabled:bg-brand/50 disabled:cursor-not-allowed text-foreground font-medium py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2">
                  {editLoading ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white" />Saving...</>
                  ) : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Site Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl w-full max-w-sm border border-border shadow-2xl">
            <div className="px-6 py-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-destructive" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Delete Site</h2>
                  <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
                </div>
              </div>

              <p className="text-sm text-foreground mb-1">
                Are you sure you want to delete <span className="font-semibold text-foreground">{deleteTarget.name}</span>?
              </p>
              <p className="text-xs text-muted-foreground mb-4 font-mono">{deleteTarget.nodeId}</p>

              {deleteTarget.status === 'online' && (
                <div className="bg-[var(--warning)]/10 border border-[var(--warning)]/30 rounded-lg p-3 mb-4">
                  <p className="text-xs text-[var(--warning)]">⚠ This site is currently online. Deleting it will disconnect the tunnel and it will no longer be able to connect.</p>
                </div>
              )}

              {deleteError && (
                <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm px-3 py-2 rounded-lg mb-4">{deleteError}</div>
              )}

              <div className="flex gap-3">
                <button onClick={() => setDeleteTarget(null)} className="flex-1 bg-muted hover:bg-accent text-foreground font-medium py-2.5 rounded-lg transition-colors text-sm">Cancel</button>
                <button onClick={handleDeleteNode} disabled={deleteLoading} className="flex-1 bg-destructive hover:bg-destructive/90 disabled:bg-destructive/50 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2">
                  {deleteLoading ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white" />Deleting...</>
                  ) : 'Delete Site'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
